import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, CreditCard, Banknote, Save } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";

interface PaymentMethod {
  id: string;
  method_type: 'cash' | 'paypal' | 'stripe';
  is_active: boolean;
  is_sandbox: boolean;
  settings: any;
}

const AdminPaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .order('method_type');

      if (error) throw error;
      setPaymentMethods((data || []) as PaymentMethod[]);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los métodos de pago",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentMethod = async (methodType: string, updates: Partial<PaymentMethod>) => {
    setSaving(methodType);
    try {
      const { error } = await supabase
        .from('payment_methods')
        .update(updates)
        .eq('method_type', methodType);

      if (error) throw error;

      setPaymentMethods(prev => 
        prev.map(method => 
          method.method_type === methodType 
            ? { ...method, ...updates }
            : method
        )
      );

      toast({
        title: "Guardado",
        description: "Configuración actualizada exitosamente",
      });
    } catch (error) {
      console.error('Error updating payment method:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la configuración",
        variant: "destructive",
      });
    } finally {
      setSaving(null);
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <Banknote className="h-5 w-5" />;
      case 'paypal':
        return <DollarSign className="h-5 w-5" />;
      case 'stripe':
        return <CreditCard className="h-5 w-5" />;
      default:
        return <DollarSign className="h-5 w-5" />;
    }
  };

  const getMethodTitle = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Pago en Efectivo';
      case 'paypal':
        return 'PayPal';
      case 'stripe':
        return 'Stripe';
      default:
        return method;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
          Métodos de Pago
        </h1>
        <p className="text-muted-foreground mt-2">
          Configura los métodos de pago disponibles para tus clientes
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        {paymentMethods.map((method) => (
          <Card key={method.id} className="transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    {getMethodIcon(method.method_type)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{getMethodTitle(method.method_type)}</CardTitle>
                    <CardDescription>
                      {method.method_type === 'cash' && 'Pagos en efectivo al momento del servicio'}
                      {method.method_type === 'paypal' && 'Pagos a través de PayPal con API REST'}
                      {method.method_type === 'stripe' && 'Pagos con tarjeta de crédito/débito'}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Active Switch */}
              <div className="flex items-center justify-between">
                <Label htmlFor={`active-${method.method_type}`} className="text-sm font-medium">
                  Activado
                </Label>
                <Switch
                  id={`active-${method.method_type}`}
                  checked={method.is_active}
                  onCheckedChange={(checked) => 
                    updatePaymentMethod(method.method_type, { is_active: checked })
                  }
                />
              </div>

              <Separator />

              {/* Method-specific configurations */}
              {method.method_type === 'paypal' && (
                <div className="space-y-4">
                   <div className="space-y-2">
                    <Label htmlFor={`paypal-email-${method.id}`}>Email de PayPal</Label>
                    <Input
                      id={`paypal-email-${method.id}`}
                      type="email"
                      placeholder="tu-email@paypal.com"
                      value={method.settings?.email || ''}
                      onChange={(e) => {
                        const updatedSettings = { ...method.settings, email: e.target.value };
                        setPaymentMethods(prev =>
                          prev.map(m =>
                            m.id === method.id ? { ...m, settings: updatedSettings } : m
                          )
                        );
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`paypal-client-id-${method.id}`}>Client ID</Label>
                    <Input
                      id={`paypal-client-id-${method.id}`}
                      type="text"
                      placeholder="PayPal Client ID"
                      value={method.settings?.client_id || ''}
                      onChange={(e) => {
                        const updatedSettings = { ...method.settings, client_id: e.target.value };
                        setPaymentMethods(prev =>
                          prev.map(m =>
                            m.id === method.id ? { ...m, settings: updatedSettings } : m
                          )
                        );
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`paypal-client-secret-${method.id}`}>Client Secret</Label>
                    <Input
                      id={`paypal-client-secret-${method.id}`}
                      type="password"
                      placeholder="PayPal Client Secret"
                      value={method.settings?.client_secret || ''}
                      onChange={(e) => {
                        const updatedSettings = { ...method.settings, client_secret: e.target.value };
                        setPaymentMethods(prev =>
                          prev.map(m =>
                            m.id === method.id ? { ...m, settings: updatedSettings } : m
                          )
                        );
                      }}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Modo de PayPal</Label>
                    <RadioGroup
                      value={method.is_sandbox ? 'sandbox' : 'live'}
                      onValueChange={(value) =>
                        updatePaymentMethod(method.method_type, { is_sandbox: value === 'sandbox' })
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sandbox" id={`paypal-sandbox-${method.id}`} />
                        <Label htmlFor={`paypal-sandbox-${method.id}`}>Sandbox (Pruebas)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="live" id={`paypal-live-${method.id}`} />
                        <Label htmlFor={`paypal-live-${method.id}`}>Live (Producción)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    onClick={() => updatePaymentMethod(method.method_type, { settings: method.settings })}
                    disabled={saving === method.method_type}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving === method.method_type ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                </div>
              )}

              {method.method_type === 'stripe' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`stripe-pk-${method.id}`}>Clave Pública (PK)</Label>
                    <Input
                      id={`stripe-pk-${method.id}`}
                      type="text"
                      placeholder="pk_test_... o pk_live_..."
                      value={method.settings?.public_key || ''}
                      onChange={(e) => {
                        const updatedSettings = { ...method.settings, public_key: e.target.value };
                        setPaymentMethods(prev =>
                          prev.map(m =>
                            m.id === method.id ? { ...m, settings: updatedSettings } : m
                          )
                        );
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`stripe-sk-${method.id}`}>Clave Secreta (SK)</Label>
                    <Input
                      id={`stripe-sk-${method.id}`}
                      type="password"
                      placeholder="sk_test_... o sk_live_..."
                      value={method.settings?.secret_key || ''}
                      onChange={(e) => {
                        const updatedSettings = { ...method.settings, secret_key: e.target.value };
                        setPaymentMethods(prev =>
                          prev.map(m =>
                            m.id === method.id ? { ...m, settings: updatedSettings } : m
                          )
                        );
                      }}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Modo de Stripe</Label>
                    <RadioGroup
                      value={method.is_sandbox ? 'test' : 'live'}
                      onValueChange={(value) =>
                        updatePaymentMethod(method.method_type, { is_sandbox: value === 'test' })
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="test" id={`stripe-test-${method.id}`} />
                        <Label htmlFor={`stripe-test-${method.id}`}>Test (Pruebas)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="live" id={`stripe-live-${method.id}`} />
                        <Label htmlFor={`stripe-live-${method.id}`}>Live (Producción)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    onClick={() => updatePaymentMethod(method.method_type, { settings: method.settings })}
                    disabled={saving === method.method_type}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving === method.method_type ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                </div>
              )}

              {method.method_type === 'cash' && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">
                    No requiere configuración adicional.<br />
                    Solo activa o desactiva este método de pago.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentMethods;